import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText } from 'lucide-react';

interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms';
}

const PolicyModal: React.FC<PolicyModalProps> = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  const privacyContent = (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Shield className="w-8 h-8 text-neon-green" />
        <div>
          <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
          <p className="text-gray-400">Last updated: December 15, 2024</p>
        </div>
      </div>

      <div className="space-y-6 text-gray-300">
        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">Introduction</h3>
          <p className="leading-relaxed">
            Welcome to PicksCity ("we," "our," or "us"). This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our sports content and analysis
            platform ("Service"). Please read this privacy policy carefully.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">Information We Collect</h3>
          <div className="space-y-3">
            <p><strong className="text-white">Personal Information:</strong> Name, email address, phone number, and payment information.</p>
            <p><strong className="text-white">Usage Data:</strong> Information about how you use our service, including picks viewed, subscriptions, and interactions.</p>
            <p><strong className="text-white">Device Information:</strong> IP address, browser type, device identifiers, and location data.</p>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">How We Use Your Information</h3>
          <ul className="list-disc list-inside space-y-2">
            <li>Provide and maintain our service</li>
            <li>Process payments and subscriptions</li>
            <li>Send you picks and notifications</li>
            <li>Improve our platform and user experience</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">Information Sharing</h3>
          <p className="leading-relaxed">
            We do not sell, trade, or otherwise transfer your personal information to third parties without your consent,
            except as described in this policy. We may share information with trusted partners who assist us in operating
            our platform, conducting business, or serving users.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">Data Security</h3>
          <p className="leading-relaxed">
            We implement appropriate security measures to protect your personal information against unauthorized access,
            alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">Your Rights</h3>
          <p className="leading-relaxed">
            You have the right to access, update, or delete your personal information. You may also opt out of certain
            communications from us. Contact us to exercise these rights.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">Contact Us</h3>
          <p className="leading-relaxed">
            If you have questions about this Privacy Policy, please contact us at privacy@pickscity.com.
          </p>
        </section>
      </div>
    </div>
  );

  const termsContent = (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <FileText className="w-8 h-8 text-neon-green" />
        <div>
          <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
          <p className="text-gray-400">Last updated: December 15, 2024</p>
        </div>
      </div>

      <div className="space-y-6 text-gray-300">
        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">1. Agreement to Terms</h3>
          <p className="leading-relaxed">
            By accessing and using PicksCity, you accept and agree to be bound by these Terms of Service.
            These Terms constitute a legally binding agreement between you and PicksCity.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">2. Age Requirements and Eligibility</h3>
          <div className="space-y-3">
            <p><strong className="text-white">You must be at least 18 years old to use PicksCity.</strong></p>
            <p>By using our service, you represent and warrant that:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>You are at least 18 years of age</li>
              <li>You are legally permitted to access sports content in your jurisdiction</li>
              <li>You will not use our service for any illegal purposes</li>
              <li>All information you provide is accurate and truthful</li>
            </ul>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">3. Service Description</h3>
          <p className="leading-relaxed">
            PicksCity provides sports content, analysis, and related information. Our service is for entertainment
            and informational purposes only. We do not guarantee the accuracy of picks or predictions.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">4. User Responsibilities</h3>
          <ul className="list-disc list-inside space-y-2">
            <li>Maintain the confidentiality of your account</li>
            <li>Use the service only for lawful purposes</li>
            <li>Respect intellectual property rights</li>
            <li>Not share subscription content with non-subscribers</li>
            <li>Comply with all applicable laws in your jurisdiction</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">5. Payment and Subscriptions</h3>
          <div className="space-y-3">
            <p>Subscription fees are charged in advance and are non-refundable except as required by law.</p>
            <p>You may cancel your subscription at any time, but you will continue to have access until the end of your billing period.</p>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">6. Disclaimer</h3>
          <p className="leading-relaxed">
            <strong className="text-white">Sports analysis is for informational purposes only.</strong> PicksCity provides
            content and analysis for entertainment purposes only. We are not responsible for any financial decisions
            based on our content.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">7. Limitation of Liability</h3>
          <p className="leading-relaxed">
            PicksCity shall not be liable for any indirect, incidental, special, or consequential damages arising from
            your use of our service.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neon-green mb-3">8. Contact Information</h3>
          <p className="leading-relaxed">
            For questions about these Terms of Service, please contact us at support@pickscity.com.
          </p>
        </section>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-dark-900 rounded-xl border border-neon-green/20 w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-700">
            <h1 className="text-xl font-bold text-white">
              {type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
            </h1>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-dark-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {type === 'privacy' ? privacyContent : termsContent}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-dark-700 bg-dark-800/50">
            <button
              onClick={onClose}
              className="w-full px-4 py-3 bg-neon-green text-black rounded-lg hover:bg-green-400 transition-colors font-semibold"
            >
              I Understand
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PolicyModal;